import React, { useMemo, useState, useEffect, useRef, CSSProperties } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from "react-router-dom";
import { ThiSinh, Test, ApiResponse, Subject, Question, Student } from "../../../interfaces";
import useApiService from "../../../services/useApiService";
import ResultModal from './ResultModal/ResultModal';
import { toast } from 'react-toastify';
import './FinalExamForm.scss';
import { VirtualDPad } from './VirtualDPad';
import { VirtualNumpad } from './VirtualNumpad';
import { ENVIRONMENT_CONFIGS, getCurrentEnvironment } from '../../../core/config/environment';

// Câu điểm liệt theo hạng — sai 1 câu bất kỳ → TRƯỢT dù đủ điểm
// Chỉ áp dụng cho hạng xe máy: A, A1m, Am. Mỗi bộ đề chỉ chứa 1 câu điểm liệt.
const DIEM_LIET_NUMS = [19, 20, 21, 22, 24, 26, 27, 28, 30, 47, 48, 52, 53, 63, 64, 65, 68, 70, 71, 72];
const DIEM_LIET_SET = new Set(DIEM_LIET_NUMS);
const DIEM_LIET_RANKS = new Set(['A', 'A1m', 'Am']);

const ITEMS_PER_COLUMN = 15;

const FinalExamForm: React.FC = () => {
  const { get, post, put, del } = useApiService();
  const navigate = useNavigate();
  const location = useLocation();
  let { IDThiSinh, IDSubject } = location.state as { IDThiSinh: number, IDSubject: number };
  
  const [studentNow, setStudentNow] = useState<ThiSinh | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState<number[][]>([]);
  const [subject, setSubject] = useState<Subject | null>(null);
  const [arrQuestion, setArrQuestion] = useState<any[]>([]);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [showAnswers, setShowAnswers] = useState(false);
  const [isExamFinished, setIsExamFinished] = useState(false);
  const [timeOut, setTimeOut] = useState(false);
  const [testRandom, setTestRandom] = useState<number | null>(null);
  const [testCode, setTestCode] = useState<string | null>(null);
  const [nextSubjectName, setNextSubjectName] = useState<string | null>(null);
  const [untestedSubjects, setUntestedSubjects] = useState<Subject[]>([]); // Lưu danh sách môn chưa thi
  const [criticalNote, setCriticalNote] = useState<string | null>(null);
  
  const [itemsPerColumn, setItemsPerColumn] = useState(ITEMS_PER_COLUMN);
  const [isMobileLandscape, setIsMobileLandscape] = useState(false);
  const [isFakeLandscape, setIsFakeLandscape] = useState(false);
  const [showConfirmEnd, setShowConfirmEnd] = useState(false);

  // WebSocket: gửi JOIN_EXAM để server đếm người đang thi
  useEffect(() => {
    const wsUrl = ENVIRONMENT_CONFIGS[getCurrentEnvironment()]?.WS_BASE_URL;
    if (!wsUrl) return;
    const ws = new WebSocket(wsUrl);
    ws.onopen = () => ws.send(JSON.stringify({ type: 'JOIN_EXAM' }));
    return () => ws.close();
  }, []);

  // Request fullscreen on mobile to hide address bar
  useEffect(() => {
    const requestFullscreen = async () => {
      if (!document.fullscreenElement && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
        try {
          const elem = document.documentElement;
          if (elem.requestFullscreen) {
            await elem.requestFullscreen();
          } else if ((elem as any).webkitRequestFullscreen) {
            await (elem as any).webkitRequestFullscreen();
          } else if ((elem as any).mozRequestFullScreen) {
            await (elem as any).mozRequestFullScreen();
          } else if ((elem as any).msRequestFullscreen) {
            await (elem as any).msRequestFullscreen();
          }
        } catch (err) {
          console.log('Fullscreen request failed:', err);
        }
      }
    };
    
    // Delay to avoid blocking initial render
    const timer = setTimeout(requestFullscreen, 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const updateExamLayout = () => {
      // Dùng pointer: coarse để detect thiết bị cảm ứng (phone, tablet, iPad, NestHub)
      const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
      
      // Detect nếu cần fake landscape — mọi touch device (phone/tablet/fold) ở portrait
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isPortrait = height > width;
      const needsFakeLandscape = isTouchDevice && isPortrait && width <= 1024;
      
      // Detect màn hình nhỏ landscape (tablet landscape hoặc desktop resize nhỏ)
      const isLandscape = width > height;
      const isSmallLandscape = isLandscape && width <= 950;
      
      const shouldUseMobileLayout = isTouchDevice || isSmallLandscape;
      setItemsPerColumn(ITEMS_PER_COLUMN);
      
      // isMobileLandscape: dùng cho layout landscape (real hoặc fake)
      setIsMobileLandscape(shouldUseMobileLayout || needsFakeLandscape);
      setIsFakeLandscape(needsFakeLandscape);
    };

    updateExamLayout();
    window.addEventListener('resize', updateExamLayout);
    window.addEventListener('orientationchange', updateExamLayout);
    return () => {
      window.removeEventListener('resize', updateExamLayout);
      window.removeEventListener('orientationchange', updateExamLayout);
    };
  }, []);

  // Lock document scroll when fake-landscape is active so the browser
  // doesn't show its own scrollbar on the physical right side of the screen.
  useEffect(() => {
    if (isFakeLandscape) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, [isFakeLandscape]);

  const questionColumns = useMemo(() => {
    const columns: Array<{ start: number; items: any[] }> = [];
    for (let start = 0; start < arrQuestion.length; start += itemsPerColumn) {
      columns.push({
        start,
        items: arrQuestion.slice(start, start + itemsPerColumn),
      });
    }
    return columns;
  }, [arrQuestion, itemsPerColumn]);

  const desktopExamLayoutStyle = useMemo((): CSSProperties => {
    const colCount = Math.max(1, questionColumns.length);
    // Desktop columns are wider; mobile landscape columns are narrower
    const colW = isMobileLandscape ? 72 : 115;
    const GAP = 8;
    // Total horizontal padding consumed by right-exam (16px) + nav-container (16px)
    const TOTAL_H_PAD = 32;
    const rightW = colCount * colW + Math.max(0, colCount - 1) * GAP + TOTAL_H_PAD;
    return {
      ['--right-exam-width' as any]: `${rightW}px`,
      ['--col-width' as any]: `${colW}px`,
      ['--question-column-count' as any]: `${colCount}`,
    };
  }, [questionColumns.length, isMobileLandscape]);

  const [showMobileList, setShowMobileList] = useState<boolean>(false);
  const questionNavContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const navContainer = questionNavContainerRef.current;
    if (!navContainer) return;

    const currentQuestionElement = navContainer.querySelector('.question-btn.current') as HTMLElement | null;
    if (!currentQuestionElement) return;

    currentQuestionElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }, [currentQuestion]);
  // Khởi tạo bài thi ban đầu
  useEffect(() => {
    const initializeExam = async () => {
      try {
        const response = await get<ApiResponse<Student[]>>(`/api/students?IDThiSinh=${IDThiSinh}`);
        if (response.DT.length === 0) {
          toast.error("Không tìm thấy thí sinh.");
          navigate('/testStudent');
          return;
        }

        const student: ThiSinh = response.DT[0];
        setStudentNow(student);

        // --- Temporarily disabled: server-side exam completion check ---
        // Since exams are not saved to server, we skip checking completed exams.
        // Re-enable when server-side exam storage is restored.
        // const examSubjectIds = student?.exams?.map(exam => Number(exam.IDSubject)) || [];
        // const subjectsNotTested: Subject[] = student?.rank?.subjects?.filter(
        //   (subject: any) => !examSubjectIds.includes(subject.id)
        // ) || [];
        // if (subjectsNotTested.length === 0) { ... }

        // All subjects are available since no exams are saved
        const allSubjects: Subject[] = student?.rank?.subjects || [];
        const subjectId = allSubjects.some(e => e.id === IDSubject) ? IDSubject : allSubjects[0]?.id;
        if (!subjectId) {
          toast.error("Không tìm thấy môn thi.");
          navigate('/testStudent');
          return;
        }
        setUntestedSubjects(allSubjects.filter(e => e.id !== subjectId));
        await setupExam(subjectId);

      } catch (error) {
        console.error("Lỗi khi khởi tạo bài thi:", error);
        toast.error("Không thể khởi tạo bài thi.");
        navigate('/testStudent');
      }
    };

    initializeExam();
  }, [IDThiSinh]);

  const setupExam = async (subjectId: number) => {
    try {
      // First try the cached endpoint; if it comes back empty, retry once
      // with ?force=1 to bypass any in-memory cache that might be holding a
      // stale empty entry from before the recent backend cache fix.
      let getRandomTest = await get<ApiResponse<Subject[]>>(`/api/subject/get-test/${subjectId}`);
      if (!getRandomTest.DT || getRandomTest.DT.length === 0) {
        getRandomTest = await get<ApiResponse<Subject[]>>(`/api/subject/get-test/${subjectId}?force=1`);
      }
      if (!getRandomTest.DT || getRandomTest.DT.length === 0) {
        // After the force-refresh the DB genuinely has no tests for this
        // subject — this is a data setup issue, not a transient bug.
        toast.error("Môn học chưa có bộ đề thi nào trong hệ thống. Liên hệ quản trị viên để import bộ đề.", { autoClose: 8000 });
        navigate('/testStudent');
        return;
      }

      // Try every test in random order so a single empty/deleted test set
      // doesn't bail out the whole exam — fixes the "phải chuyển khóa khác
      // rồi quay lại mới chạy" symptom when the backend cache holds stale
      // test IDs after a course/subject edit.
      const dataTest = [...getRandomTest.DT].sort(() => Math.random() - 0.5);
      let varTest: ApiResponse<Test[]> | null = null;
      let testRandom: number | null = null;
      for (const candidate of dataTest) {
        const resp = await get<ApiResponse<Test[]>>(`/api/test/get-test/${candidate.id}`);
        const test = resp?.DT?.[0];
        if (test && test.subject && Array.isArray(test.questions) && test.questions.length > 0) {
          varTest = resp;
          testRandom = candidate.id;
          break;
        }
      }

      if (!varTest || testRandom === null) {
        toast.error("Dữ liệu bài thi không hợp lệ.");
        navigate('/testStudent');
        return;
      }

      const varSubject = varTest.DT[0].subject as Subject;
      const varArrQuestion = varTest.DT[0].questions;

      if (!varSubject || !varArrQuestion?.length) {
        toast.error("Dữ liệu bài thi không hợp lệ.");
        navigate('/testStudent');
        return;
      }

      // Defensive check — server-side validation now blocks this on save,
      // and the migration auto-fixes legacy rows, but if a stale subject
      // somehow still has threshold > numberofquestion the exam would be
      // unwinnable. Surface a clear error instead of letting the user take
      // an impossible test.
      if (varSubject.threshold > varArrQuestion.length) {
        toast.error(`Cấu hình môn học không hợp lệ: cần đạt ${varSubject.threshold}/${varArrQuestion.length} câu. Liên hệ quản trị viên.`);
        navigate('/testStudent');
        return;
      }

      const questionsTest = varArrQuestion.map((e: Question) => ({
        ...e,
        options: Array((e as any).totalOptions || 4).fill(""),
      }));

      // Thiết lập tất cả state cùng lúc
      setSubject(varSubject);
      setArrQuestion(questionsTest);
      setSelectedOptions(new Array(questionsTest.length).fill([]));
      setTimeRemaining(varSubject.timeFinish * 60);
      setTestRandom(testRandom);
      setTestCode(varTest.DT[0].code);
      setCurrentQuestion(0);
      setIsExamFinished(false);
      setTimeOut(false);
      setShowResult(false);
    } catch (error) {
      console.error("Lỗi trong setupExam:", error);
      toast.error("Không thể thiết lập bài thi.");
      navigate('/testStudent');
    }
  };

  // Bộ đếm thời gian — KHÔNG khởi động cho tới khi đề thi đã load
  // (subject !== null). Trước đó timeRemaining state mặc định là 0 và
  // interval 1s sẽ fire điều kiện `prevTime <= 1` → setTimeOut(true) →
  // auto kết bài NGAY khi component mount. Gate vào `subject` để fix.
  useEffect(() => {
    if (!subject || timeRemaining <= 0) return;
    const intervalId = setInterval(() => {
      setTimeRemaining((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(intervalId);
          if (!isExamFinished) {
            setTimeOut(true);
          }
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExamFinished, subject]);

  useEffect(() => {
    if (timeOut) {
      setIsExamFinished(true);
      handleEndExam();
    }
  }, [timeOut]);


  const handleEndExam = () => {
    setShowConfirmEnd(true);
  };

  const handleConfirmEnd = async () => {
    setShowConfirmEnd(false);
    if (!isExamFinished) {
      setIsExamFinished(true);
      setTimeRemaining(0);
      try {
        await handleFinishExam();
      } catch (error) {
        console.error("Lỗi khi kết thúc bài thi:", error);
        toast.error("Có lỗi xảy ra khi kết thúc bài thi.");
      }
    }
  };

  const handleKeyPress = (event: KeyboardEvent) => {
    const optionIndex = parseInt(event.key);
    // console.log('check optionIndex', optionIndex)
    if (optionIndex >= 0 && optionIndex <= arrQuestion[currentQuestion].options.length) {
      toggleOption(currentQuestion, optionIndex);
    }

    if (event.key === 'ArrowUp') {
      handleQuestionChange((currentQuestion - 1 + arrQuestion.length) % arrQuestion.length);
    } else if (event.key === 'ArrowDown') {
      handleQuestionChange((currentQuestion + 1) % arrQuestion.length);
    }

  };

  const toggleOption = (questionIndex: number, optionIndex: number) => {
    setSelectedOptions((prev) => {
      const newSelections = [...prev];
      const optionsForCurrentQuestion = newSelections[questionIndex];
      if (optionsForCurrentQuestion.includes(optionIndex)) {
        newSelections[questionIndex] = optionsForCurrentQuestion.filter((opt) => opt !== optionIndex);
      } else {
        newSelections[questionIndex] = [...optionsForCurrentQuestion, optionIndex];
      }
      return newSelections;
    });
  };

  const handleQuestionChange = (index: number) => {
    setCurrentQuestion(index);
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [currentQuestion, selectedOptions]);

  // Chuyển đổi giây thành định dạng phút:giây
  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  function arraysAreEqual(arr1: any[], arr2: any[]): boolean {
    // Sắp xếp mảng và so sánh
    arr1.sort();
    arr2.sort();

    return JSON.stringify(arr1) === JSON.stringify(arr2);
  }

  function convertStringsToNumbers(arr: string[]): number[] {
    return arr.map(item => {
      const number = Number(item);
      return isNaN(number) ? 0 : number; // Trả về 0 nếu không phải số hợp lệ
    });
  }

  const handleFinishExam = async () => {
    try {
      if (!subject) {
        return;
      }
      let calculatedScore = 0;
      const stringAnswerlist: string[] = [];

      const rank = studentNow?.loaibangthi || '';
      const hasDiemLiet = DIEM_LIET_RANKS.has(rank);
      const failedCritical: { examPos: number; bankNum: number }[] = [];

      arrQuestion.forEach((question, index) => {
        const isCorrect = arraysAreEqual(selectedOptions[index], convertStringsToNumbers(question?.answer?.toString()?.split(',')));
        if (isCorrect) {
          calculatedScore++;
        } else if (hasDiemLiet && DIEM_LIET_SET.has(question.number)) {
          failedCritical.push({ examPos: index + 1, bankNum: question.number });
        }
        stringAnswerlist.push(selectedOptions[index].join('-'));
      });

      const isFailedCritical = failedCritical.length > 0;
      const finalResult = (calculatedScore < subject.threshold || isFailedCritical) ? "TRƯỢT" : "ĐẠT";

      // --- Temporarily disabled: exam result not saved to server ---
      // const resCreateExam = await post<ApiResponse>("/api/exam/create-exam", {
      //   IDThisinh: IDThiSinh,
      //   IDTest: testRandom,
      //   answerlist: stringAnswerlist.join(','),
      //   point: calculatedScore,
      //   result: finalResult,
      //   IDSubject: subject?.id,
      // });
      //
      // if (resCreateExam.EC === 0) toast.success(resCreateExam.EM);
      // else if (resCreateExam.EC === 1) toast.warn(resCreateExam.EM);
      // else toast.error(resCreateExam.EM);

      // Show result locally instead
      if (finalResult === "ĐẠT") {
        toast.success(`Chúc mừng bạn đã ĐẠT với số điểm là: ${calculatedScore}`);
      } else {
        toast.warn(`Bạn đã TRƯỢT với số điểm là: ${calculatedScore}`);
      }

      // --- Temporarily disabled: server-side untested subject tracking ---
      // const response = await get<ApiResponse<Student[]>>(`/api/students?IDThiSinh=${IDThiSinh}`);
      // const updatedStudent: ThiSinh = response.DT[0];
      // const examSubjectIds = updatedStudent?.exams?.map(exam => Number(exam.IDSubject)) || [];
      // const updatedUntestedSubjects: Subject[] = updatedStudent?.rank?.subjects?.filter(
      //   (subject: any) => !examSubjectIds.includes(subject.id)
      // ) || [];
      // if(updatedUntestedSubjects.length == 0){
      //   await post(`/api/students/update-processtest`, {
      //     IDThiSinh,
      //     processtest: 3,
      //   });
      // }else{
      //   await post(`/api/students/update-processtest`, {
      //     IDThiSinh,
      //     processtest: 1,
      //   });
      // }

      // Compute untested subjects locally (no server round-trip)
      const currentSubjectId = subject.id;
      const updatedUntestedSubjects = untestedSubjects.filter(s => s.id !== currentSubjectId);

      setUntestedSubjects(updatedUntestedSubjects);
      setNextSubjectName(updatedUntestedSubjects.length > 0 ? updatedUntestedSubjects[0].name : null);

      setScore(calculatedScore);
      setCriticalNote(isFailedCritical
        ? `Sai câu điểm liệt: ${failedCritical.map(f => `câu số ${f.examPos}`).join(', ')}`
        : null);
      setShowResult(true);
    } catch (error) {
      console.error("Lỗi khi ghi nhận kết quả:", error);
      toast.error("Không thể ghi nhận kết quả thi.");
    }
  };

  const handleViewAnswers = () => {
    setShowAnswers(true);
  };

  const handleCloseResult = async () => {
    // --- Temporarily disabled: processtest status update ---
    // try {
    //   await post(`/api/students/update-processtest`, {
    //     IDThiSinh,
    //     processtest: 1,
    //   });
    // } catch (error) {
    //   console.error("Lỗi khi reset trạng thái:", error);
    // }
    setShowResult(false);
  };

  // const handlePrintAnswers = () => {
  //   if (modalRef.current) {
  //     const printWindow = window.open('', '', 'height=800,width=600'); // Mở một cửa sổ mới tạm thời

  //     printWindow?.document.write('<html><head><title>In đáp án</title></head><body>');
  //     printWindow?.document.write(modalRef.current.innerHTML); // Lấy nội dung modal và viết vào cửa sổ mới
  //     printWindow?.document.write('</body></html>');

  //     printWindow?.document.close(); // Đóng tài liệu để bắt đầu in
  //     printWindow?.focus(); // Đảm bảo cửa sổ được focus trước khi in
  //     printWindow?.print(); // Gọi lệnh in trực tiếp
  //     printWindow?.close(); // Đóng cửa sổ sau khi in xong
  //   }
  // };

  const handleNextExam = async () => {
    try {
      if (untestedSubjects.length === 0) {
        toast.success("Bạn đã hoàn thành tất cả các bài thi!");
        // --- Temporarily disabled: processtest status update ---
        // await post(`/api/students/update-processtest`, {
        //   IDThiSinh,
        //   processtest: 3,
        // });
        navigate('/testStudent');
        return;
      } else {
        const nextSubjectId = untestedSubjects[0].id;
        setUntestedSubjects(prev => prev.filter(subject => subject.id !== nextSubjectId));
        setNextSubjectName(untestedSubjects.length ? untestedSubjects[0].name : null);
        await setupExam(nextSubjectId);
        // --- Temporarily disabled: processtest status update ---
        // await post(`/api/students/update-processtest`, {
        //   IDThiSinh,
        //   processtest: 2,
        // });
      }
    } catch (error) {
      console.error("Lỗi khi tạo bài thi kế tiếp:", error);
      navigate('/testStudent');
    }
  };

  if (!studentNow || !arrQuestion.length || !subject) {
    return <div>Loading...</div>; // Hiển thị loading khi chưa có dữ liệu
  }

  const getQuestionImage = (number: number | undefined) => {
    if (!number) return null;
    try {
      return require(`../../../assets/600question_2025/${number}.jpg`);
    } catch (error) {
      console.error(`Không tìm thấy ảnh: ${number}.jpg`);
      return null;
    }
  };

  return (
    <>
      <div className={`exam-rotate-wrapper ${isFakeLandscape ? 'fake-landscape' : ''}`}>
      <div className={`exam-container`} style={desktopExamLayoutStyle}>
        <div className="left-exam">        
        <div className="question-section">
          <div className="mobile-current-question-info">
            {currentQuestion + 1}/{arrQuestion.length}
          </div>
          {(() => {
            const imageSrc = getQuestionImage(arrQuestion[currentQuestion]?.number);
            return imageSrc ? (
              <img
                src={imageSrc}
                alt={`Câu hỏi ${arrQuestion[currentQuestion]?.number}`}
              />
            ) : (
              <div>Không tìm thấy ảnh câu hỏi {arrQuestion[currentQuestion]?.number}</div>
            );
          })()}
        </div>
        
        <div className="virtual-controls">
          <div className="virtual-controls__dpad">
            <VirtualDPad
              currentQuestion={currentQuestion}
              itemsPerColumn={itemsPerColumn}
              totalQuestions={arrQuestion.length}
              onQuestionChange={handleQuestionChange}
              disableHorizontal={isMobileLandscape}
            />
          </div>
          <div className="virtual-controls__numpad">
            <VirtualNumpad
              currentQuestion={currentQuestion}
              selectedOptions={selectedOptions}
              toggleOption={toggleOption}
            />
          </div>
        </div>
        
        <div className="footer">
          <div className="left">
            <img src={'data:image/jpeg;base64,' + studentNow?.Anh} className='image-hv' alt="" />
          </div>
          <div className="middle">
            <div className="subject">
              <h5>Môn thi: {subject?.name} ({testCode})</h5>
            </div>
            <div className="rank">
              <h5>Số Báo Danh: {studentNow?.khoahoc_thisinh?.SoBaoDanh}</h5>
            </div>
            <h5>Hạng: {studentNow?.loaibangthi}</h5>
            <h5>Họ và tên: {studentNow?.HoTen}</h5>
            <h5>Số CCCD: {studentNow?.SoCMT}</h5>
          </div>
          <div className="right">
            <img src={require(`../../../assets/logo.jpg`)} className='logo' alt="" />
          </div>
        </div>
      </div>
      <div className="right-exam">
        <div className="sidebar-section">
          <div className="top">
            <div className="time-remaining">
              Time:<span>  {timeRemaining === 0 ? (
                <span>Hết thời gian</span>
              ) : (
                <span>{formatTime(timeRemaining)}</span>
              )}</span>
            </div>
            <div
              className="question-nav-container"
              ref={questionNavContainerRef}
            >
              {questionColumns.map((column, columnIndex) => (
                <div className="question-nav" key={columnIndex}>
                  {column.items.map((_, questionIndex) => {
                    const globalIndex = column.start + questionIndex;
                    return (
                      <div
                        key={globalIndex}
                        className={`question-btn ${selectedOptions[globalIndex]?.length > 0 ? 'answered' : 'unanswered'} ${currentQuestion === globalIndex ? 'current' : ''}`}
                        onClick={() => handleQuestionChange(globalIndex)}
                      >
                        <div className="question-number">{globalIndex + 1}</div>
                        <div className="answer-options">
                          {arrQuestion[globalIndex].options.map((_: any, optIndex: number) => (
                            <div key={optIndex} className="option-cell">
                              <span>{optIndex + 1}</span>
                              <input
                                type="checkbox"
                                checked={selectedOptions[globalIndex]?.includes(optIndex + 1)}
                                onChange={() => toggleOption(globalIndex, optIndex + 1)}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          <button className="end-exam-btn" onClick={handleEndExam}>KẾT THÚC</button>
        </div>
      </div>
    </div>
    </div>{/* exam-rotate-wrapper */}

      {showConfirmEnd && ReactDOM.createPortal(
        <div className={`confirm-end-overlay${isFakeLandscape ? ' fake-landscape' : ''}`}>
          <div className="confirm-end-box">
            <h3 className="confirm-end-title">Nộp bài</h3>
            <p className="confirm-end-msg">Bạn có chắc chắn muốn nộp bài và kết thúc thi không?</p>
            <div className="confirm-end-btns">
              <button className="confirm-end-btn confirm-end-btn--ok" onClick={handleConfirmEnd}>Có</button>
              <button className="confirm-end-btn confirm-end-btn--cancel" autoFocus onClick={() => setShowConfirmEnd(false)}>Không</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showResult && (
        <ResultModal
          score={score}
          totalQuestions={arrQuestion.length}
          correctAnswers={score}
          incorrectAnswers={arrQuestion.length - score}
          resultStatus={(score < subject!.threshold || criticalNote) ? "TRƯỢT" : "ĐẠT"}
          criticalNote={criticalNote}
          studentInfo={{
            studentID: studentNow?.khoahoc_thisinh?.SoBaoDanh || 0,
            fullName: studentNow?.HoTen || '',
            subject: subject?.name || '',
            rank: studentNow?.loaibangthi || '',
            test: testCode || '',
            CCCD: studentNow?.SoCMT || '',
            courseID: studentNow?.khoahoc_thisinh?.khoahoc?.IDKhoaHoc || '',
          }}
          onClose={handleCloseResult}
          onViewAnswers={handleViewAnswers}
          arrQuestion={arrQuestion}
          selectedOptions={selectedOptions}
          onNextExam={handleNextExam}
          nextSubjectName={nextSubjectName}
          isFakeLandscape={isFakeLandscape}
        />
      )}
    </>
  );
};

export default FinalExamForm;
